import { z } from 'zod';
import { DEFAULT_EMBLEM, EmblemSchema } from './sim/emblem.js';
import { ALL_FACTIONS } from './sim/player.js';
/**
 * Wire-shape user. Mirrors a slim, public-safe view of `userRow` in the server
 * DB. Sensitive fields (password hash, OAuth subs, ipHash) never live here.
 *
 * Returned by `GET /auth/me` and embedded in the WS `welcome` message after
 * cookie-based auth on the upgrade. Tight on purpose — richer profile data
 * (lifetime stats, reputation bars) lives on the public profile endpoint.
 */
export const PublicUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    emailVerified: z.boolean(),
    corpName: z.string().min(1),
    corpSlug: z.string().min(1),
    role: z.enum(['user', 'admin']),
    createdAt: z.string().datetime(),
    /** P3B emblem. Defaults to the fallback when an account hasn't set one. */
    emblem: EmblemSchema,
    /** Whether `/u/:corpSlug` is publicly discoverable. */
    publicProfileVisible: z.boolean(),
});
// ---------- HTTP request bodies ----------
/**
 * Signup constraints picked to be friendly without being unsafe:
 *  - email: standard z.string().email()
 *  - password: 10+ chars, no other rule. Length-as-strength beats class quotas.
 *    (Lucia + every modern auth doc agrees here.)
 *  - corpName: 3–32 chars, alphanumerics + space + hyphen. Slug derived server-side.
 */
export const CORP_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 \-]{1,30}[A-Za-z0-9]$/;
export const CORP_NAME_MIN = 3;
export const CORP_NAME_MAX = 32;
/**
 * Starting faction choice. `null` = "Independent" — no bonus, no enemies.
 * The six real faction ids come from `ALL_FACTIONS`.
 */
export const StartingFactionSchema = z
    .enum([ALL_FACTIONS[0], ...ALL_FACTIONS.slice(1)])
    .nullable();
export const SignupBodySchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(10).max(200),
    corpName: z
        .string()
        .min(CORP_NAME_MIN)
        .max(CORP_NAME_MAX)
        .regex(CORP_NAME_RE, 'Use letters, digits, spaces, and hyphens only.'),
    /** Optional — server fills in DEFAULT_EMBLEM when omitted (keeps legacy
     *  clients compiling without recompiling the wire protocol). */
    emblem: EmblemSchema.optional().default(DEFAULT_EMBLEM),
    /** Optional — `null` / undefined both mean "Independent" (no bonus). */
    startingFaction: StartingFactionSchema.optional().default(null),
});
export const LoginBodySchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(200),
});
export const PasswordResetRequestBodySchema = z.object({
    email: z.string().email().max(254),
});
export const PasswordResetConfirmBodySchema = z.object({
    token: z.string().min(20).max(200),
    newPassword: z.string().min(10).max(200),
});
export const EmailVerifyBodySchema = z.object({
    token: z.string().min(20).max(200),
});
// ---------- Public profile (P3B `/u/:corpSlug`) ----------
/**
 * The profile shape exposed at `/api/users/:corpSlug/public`. Intentionally
 * narrow — no email, no role, no internal ids leak to anonymous viewers.
 * Lifetime stats come from `player_state`; faction rep is the same dict the
 * in-game HUD uses (reputation [-100,100]).
 */
export const PublicProfileSchema = z.object({
    corpName: z.string().min(1),
    corpSlug: z.string().min(1),
    emblem: EmblemSchema,
    foundedAt: z.string().datetime(),
    startingFaction: StartingFactionSchema,
    lifetimeCredits: z.number().nonnegative(),
    researchPoints: z.number().nonnegative(),
    reputation: z.record(z.string(), z.number()),
});
export const PublicProfileResponseSchema = z.object({
    profile: PublicProfileSchema,
});
// ---------- P7A leaderboard + corps directory ──────────────────────────────
/**
 * The four axes the leaderboard ranks on. Each corresponds to one stable
 * stat from `player_state`. Adding a new axis means adding a sibling here +
 * a query branch on the server + a tab on the client.
 */
export const LeaderboardAxisSchema = z.enum([
    'credits', // lifetime credits earned
    'rp', // lifetime research points
    'contracts', // lifetime contracts fulfilled
    'topRep', // single highest faction-rep value
]);
export const ALL_LEADERBOARD_AXES = [
    'credits',
    'rp',
    'contracts',
    'topRep',
];
/**
 * One row in a leaderboard response. Includes everything the client needs
 * to render a row + click through to the profile, with no need for a
 * follow-up fetch per row.
 */
export const LeaderboardRowSchema = z.object({
    rank: z.number().int().positive(),
    corpName: z.string(),
    corpSlug: z.string(),
    emblem: EmblemSchema,
    /** The axis-specific score (credits, RP, contracts, or top rep). */
    score: z.number(),
    /** Optional: which faction the topRep score came from (only set for `topRep`). */
    topRepFaction: z.string().nullable().optional(),
    /** P12 — true when the row represents an AI Charter (NPC). The client
     *  renders a small "AI" pill next to the corp name. Default false. */
    isAI: z.boolean().optional(),
});
export const LeaderboardResponseSchema = z.object({
    axis: LeaderboardAxisSchema,
    rows: z.array(LeaderboardRowSchema),
    /** Generated-at ISO string. Reads "freshness: <ago>" on the client. */
    generatedAt: z.string().datetime(),
});
/**
 * One row in the public corps directory. Slimmer than the leaderboard row —
 * the directory is browse-by-search, not "rank 23 of 100", so it doesn't
 * need a score column.
 */
export const CorpsDirectoryRowSchema = z.object({
    corpName: z.string(),
    corpSlug: z.string(),
    emblem: EmblemSchema,
    startingFaction: z.string().nullable(),
    foundedAt: z.string().datetime(),
});
export const CorpsDirectoryResponseSchema = z.object({
    rows: z.array(CorpsDirectoryRowSchema),
    /** Total count matching the filter — drives the "showing N of M" header. */
    total: z.number().int().nonnegative(),
});
// ---------- HTTP response bodies ----------
export const AuthMeResponseSchema = z.object({
    user: PublicUserSchema.nullable(),
});
export const AuthSuccessResponseSchema = z.object({
    user: PublicUserSchema,
});
export const AuthErrorCode = z.enum([
    'INVALID_CREDENTIALS',
    'EMAIL_TAKEN',
    'CORP_NAME_TAKEN',
    'CORP_NAME_RESERVED',
    'EMAIL_NOT_VERIFIED',
    'TOKEN_INVALID',
    'TOKEN_EXPIRED',
    'TOKEN_CONSUMED',
    'WEAK_PASSWORD',
    'NOT_AUTHENTICATED',
    'NOT_AUTHORIZED',
    'RATE_LIMITED',
    'INTERNAL_ERROR',
    // P3B
    'CORP_NAME_COOLDOWN',
    'INVALID_INPUT',
    'NOT_FOUND',
    'CONFIRMATION_MISMATCH',
]);
export const AuthErrorResponseSchema = z.object({
    error: AuthErrorCode,
    message: z.string(),
});
// ---------- P3B settings endpoints ----------
/**
 * User preferences stored in `user.prefs` (jsonb). Every field is optional
 * and has a sensible default in the reader — new keys can be added without
 * a migration.
 */
export const UserPrefsSchema = z.object({
    notifyContractCompleted: z.boolean().default(true).optional(),
    notifyContractDeadlineMissed: z.boolean().default(true).optional(),
    notifyFactionRepMilestone: z.boolean().default(false).optional(),
});
export const ChangePasswordBodySchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z.string().min(10).max(200),
});
export const ChangeEmailBodySchema = z.object({
    newEmail: z.string().email().max(254),
    currentPassword: z.string().min(1).max(200),
});
export const ChangeCorpNameBodySchema = z.object({
    corpName: z
        .string()
        .min(CORP_NAME_MIN)
        .max(CORP_NAME_MAX)
        .regex(/^[A-Za-z0-9][A-Za-z0-9 \-]{1,30}[A-Za-z0-9]$/),
});
export const ChangeEmblemBodySchema = z.object({ emblem: EmblemSchema });
export const ChangeVisibilityBodySchema = z.object({ visible: z.boolean() });
export const UpdatePrefsBodySchema = z.object({ prefs: UserPrefsSchema });
export const DecommissionCharterBodySchema = z.object({
    /** User must type their exact corp name to confirm (GitHub-style). */
    confirmCorpName: z.string().min(1).max(CORP_NAME_MAX),
});
export const RequestAccountDeletionBodySchema = z.object({
    confirmCorpName: z.string().min(1).max(CORP_NAME_MAX),
    currentPassword: z.string().min(1).max(200),
});
/** Single audit log entry — trimmed to fields safe to show a non-admin user. */
export const AuditLogEntrySchema = z.object({
    id: z.string().uuid(),
    at: z.string().datetime(),
    kind: z.string(),
    category: z.enum(['auth', 'account', 'game', 'admin', 'security', 'system']),
    payload: z.record(z.string(), z.unknown()),
});
export const AuditLogResponseSchema = z.object({
    entries: z.array(AuditLogEntrySchema),
});
/** A single active session shown in /settings. */
export const SessionInfoSchema = z.object({
    id: z.string(),
    createdAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    userAgent: z.string().nullable(),
    /** True if this is the session the request came in on. */
    isCurrent: z.boolean(),
});
export const SessionsListResponseSchema = z.object({
    sessions: z.array(SessionInfoSchema),
});
// ---------- helpers ----------
/**
 * Convert a corp display name to its slug form. Deterministic and idempotent
 * — the same name always produces the same slug, which is also what the DB's
 * unique constraint enforces.
 */
export function slugifyCorpName(corpName) {
    return corpName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/**
 * Reserved corp slugs that no user can take. Matches the seed `admin` account
 * + faction names from the lore. Extend as new system identities show up.
 */
export const RESERVED_CORP_SLUGS = new Set([
    'admin',
    'system',
    'cta',
    'helion',
    'argent',
    'meridian',
    'tycho',
    'vega',
    'commonwealth',
    'sfco',
]);
//# sourceMappingURL=auth.js.map